import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { IconAlertSquareRoundedFilled, IconList, IconMap } from "@tabler/icons-react";
import Skeleton from "react-loading-skeleton";
import { classNames, useIsMounted } from "@ct-react/core";
import { ArticleType } from "@shared/url";
import { AnyArticleSortInput, SearchResult } from "@shared/graphql";
import { actions } from "../i18n/sharable-defs";
import { formatSort, useGQLSearchFormatter } from "../hooks/format-gql-variables";
import { useMediaQueryDown } from "../hooks/media-queries";
import { PaginationPayload } from "../models/search";
import * as criteriaTools from "../tools/search-params/search-criteria";
import * as paginationTools from "../tools/search-params/search-pagination";
import { AnyArticleSearch, useSearchContext } from "../contexts/search";
import { Wrapper } from "../components/layout/wrapper";
import ArticleCard from "../components/article-card";
import { MapAsyncPopupMarker, MapContainer } from "../bundles/map";
import Switch from "../components/common/switch";
import Button from "../components/common/button";
import InfoBox from "../components/common/info-box";
import SearchSort from "../components/search/sort";
import SearchedMoreFilter from "../components/search/more-filters";
import SearchedPagination from "../components/search/pagination";
import { useWishlist } from "../contexts/wishlist";
import SeoMetas from "../components/layout/seo-metas";

import Logo from "../assets/componable-svgs/Logo-texte-black.svg";

import "./search.scss";

const transDefs = defineMessages({
  meta_title_rental: { id: "search.meta-title-rental", defaultMessage: "Location de vacances à louer à Nendaz | Inter-Agence" },
  meta_title_seasonal: { id: "search.meta-title-seasonal", defaultMessage: "Location de saison à louer à Nendaz | Inter-Agence" },
  meta_title_annual: { id: "search.meta-title-annual", defaultMessage: "Locations à l'année à Nendaz | Inter-Agence" },
  meta_title_sale: { id: "search.meta-title-sale", defaultMessage: "Biens immobiliers à vendre à Nendaz | Inter-Agence" },
  meta_description_rental: { id: "search.meta-description-rental", defaultMessage: "Trouvez votre location de vacances à Nendaz. Chalets et appartements au cœur des Alpes pour un séjour confortable en toute saison." },
  meta_description_seasonal: { id: "search.meta-description-seasonal", defaultMessage: "Découvrez nos locations saisonnières à Nendaz. Appartements et chalets au cœur des Alpes pour profiter pleinement de la station été comme hiver." },
  meta_description_annual: { id: "search.meta-description-annual", defaultMessage: "Louez un appartement ou un chalet à l’année à Nendaz. Des biens confortables et bien situés pour vivre au cœur des Alpes valaisannes." },
  meta_description_sale: { id: "search.meta-description-sale", defaultMessage: "Explorez nos biens immobiliers à vendre à Nendaz. Chalets et appartements au cœur des Alpes pour concrétiser votre rêve immobilier." },
  rental_title: { id: "search.results.title-rental", defaultMessage: "Logements courte durée à louer" },
  seasonal_title: { id: "search.results.title-seasonal", defaultMessage: "Logements à louer à la saison" },
  annual_title: { id: "search.results.title-annual", defaultMessage: "Logements à louer à l'année" },
  sale_title: { id: "search.results.title-sale", defaultMessage: "Logements à vendre" },
  count: { id: "search.results.count", defaultMessage: "{count, plural, =1 {1 résultat} other {# résultats}}" },
  empty: { id: "search.results.empty", defaultMessage: "Aucun résultat" },
  emptyDetail: { id: "search.results.empty-detail", defaultMessage: "Aucun bien ne correspond à votre recherche. Ajustez vos critères ou modifiez certains de vos filtres." },
});

const ANY_SEARCH_GQL_DATA = gql`
  query search($where: AnyArticleFilterInput, $sort: AnyArticleSortInput, $first: Int, $last: Int, $before: String, $after: String) {
    search(where: $where, sort: $sort, first: $first, last: $last, before: $before, after: $after) {
      totalCount
      edges {
        cursor
        node {
          ... on RentalAccommodationArticle {
            id slug
            novelty recommended promoted
            images @limit(max: 10) { alternativeText { value matchLang } assets { url breakpoint } }
            address { city street }
            title { value }
            lowestPrice { type onDemand value }
            features { bedrooms maxGuests}
          }
          ... on SeasonalAccommodationArticle {
            id slug
            novelty recommended promoted
            images @limit(max: 10) { alternativeText { value matchLang } assets { url breakpoint } }
            address { city street }
            title { value }
            seasonalPrice: price { onDemand value }
            features { bedrooms maxGuests}
          }
          ... on AnnualAccommodationArticle {
            id slug
            novelty recommended promoted
            images @limit(max: 10) { alternativeText { value matchLang } assets { url breakpoint } }
            address { city street }
            title { value }
            annualPrice: price { onDemand value }
            features { livingSpace bedrooms }
          }
          ... on SaleAccommodationArticle {
            id slug
            novelty recommended promoted sold
            images @limit(max: 10) { alternativeText { value matchLang } assets { url breakpoint } }
            address { city street }
            title { value }
            salePrice: price { onDemand value }
            features { livingSpace bedrooms }
          }
        }
        accommodationBookingSuggestion {
          period
          priceOnDemand price { amount }
        }
      }
      accommodationMapMarkers { coordinates id slug }
      paginationInfo {
        currentPage pageCount pageSize hasNextPage hasPreviousPage
        lastPageCursor nextPageCursor previousPageCursor linkPageCursors { cursor page }
      }
    }
  }
`;

const normalizeArticlePrice = (node: any) => {
  if (!node) return node;
  const { seasonalPrice, annualPrice, salePrice, ...article } = node;
  return {
    ...article,
    price: seasonalPrice ?? annualPrice ?? salePrice
  };
};

const SearchPage = ({ type }: { type: ArticleType }) => {

  const intl = useIntl();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { popup } = useWishlist();
  const pageSize = useRef<number>(12);
  const mapRef = useRef<HTMLElement>(null);

  const [ searchParams, setSearchParams ] = useSearchParams();
  const [ ctx, dispatch ] = useSearchContext();

  const [ withMap, setWithMap ] = useState<boolean>(false);
  const { where, ...others } = useGQLSearchFormatter(pageSize.current, type, searchParams);
  const { data, loading } = useQuery<{ search: SearchResult }>(ANY_SEARCH_GQL_DATA, {
    ssr: false,
    variables: { where, ...(!!ctx[type].sort) && { sort: formatSort(type, [ ctx[type].sort ]) }, ...others }
  });

  const isDisplayUntilTablet = useMediaQueryDown("tablet");

  useEffect(() => {
    if (searchParams.size > 0 || Object.keys(ctx[type].saving).length === 0) return;
    navigate(`?${criteriaTools.rebuild(ctx[type].saving).toString()}`, { replace: true });
  }, []);

  useEffect(() => {
    if (!isMounted || !withMap || !isDisplayUntilTablet) return;
    mapRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [ withMap ]);

  const onPaginate = (value: PaginationPayload) => {
    paginationTools.rebuild(value, searchParams);
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const onSorting = (sort: AnyArticleSortInput | undefined) => {
    dispatch({ type, sort });
    paginationTools.rebuild({ direction: "after" }, searchParams);
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }

  const onFilter = (filter: AnyArticleSearch) => {
    const rebuild = new URLSearchParams();
    criteriaTools.rebuild(filter, rebuild);
    setSearchParams(rebuild);
    dispatch({ type, filter });
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }

  const wrapperClasses= classNames("search-wrapper");
  const resultClasses = classNames("search-results", { "with-map": withMap });
  const listClasses = classNames("search-listed", [{ empty: !loading && data?.search.totalCount === 0 },{"small": !loading && data?.search.totalCount! < 3}])

  return (
    <Wrapper.Centered className={wrapperClasses}>
      {type === ArticleType.RENTAL && <SeoMetas title={intl.formatMessage(transDefs.meta_title_rental)} description={intl.formatMessage(transDefs.meta_description_rental)} image={Logo} /> }
      {type === ArticleType.SEASONAL && <SeoMetas title={intl.formatMessage(transDefs.meta_title_seasonal)} description={intl.formatMessage(transDefs.meta_description_seasonal)} image={Logo} /> }
      {type === ArticleType.ANNUAL && <SeoMetas title={intl.formatMessage(transDefs.meta_title_annual)} description={intl.formatMessage(transDefs.meta_description_annual)} image={Logo} /> }
      {type === ArticleType.SALE && <SeoMetas title={intl.formatMessage(transDefs.meta_title_sale)} description={intl.formatMessage(transDefs.meta_description_sale)} image={Logo} /> }

      {popup()}
      <h1>
        {type === ArticleType.RENTAL && intl.formatMessage(transDefs.rental_title)}
        {type === ArticleType.SEASONAL && intl.formatMessage(transDefs.seasonal_title)}
        {type === ArticleType.ANNUAL && intl.formatMessage(transDefs.annual_title)}
        {type === ArticleType.SALE && intl.formatMessage(transDefs.sale_title)}
      </h1>

      <div className="search-more-filters">

        {loading
          ? <Skeleton className="btn" width={175} height={43} />
          : <SearchSort type={type}
                        criteria={ctx[type].sort}
                        onChange={onSorting} />
        }

        {loading
          ? <Skeleton className="btn" width={100} height={43} />
          : <SearchedMoreFilter criteria={criteriaTools.parse(searchParams, type)}
                                onCriteriaChange={onFilter} />
        }

        <div className="counted">
          {loading && <Skeleton width={200} />}
          {(data?.search?.totalCount || 0) > 1 && intl.formatMessage(transDefs.count, {
            count: data!.search!.totalCount
          })}
        </div>

        <div className="map-toggler">
          {intl.formatMessage(actions.showMap)}
          <Switch active={withMap} onChange={setWithMap} />
        </div>

        <Button className="primary with-icon mobile-map-toggler"
                onClick={() => setWithMap(prev => !prev)}>
          {intl.formatMessage(withMap ? actions.showList : actions.showMap)}
          {withMap ? <IconMap /> : <IconList />}
        </Button>

      </div>

      <div className={resultClasses}>
        <main>
          <div className={listClasses}>
            {(data?.search.edges || Array.from({ length: pageSize.current })).map((edge: any, i: number) => (
              <ArticleCard key={edge?.node?.id || i} loading={loading} data={edge?.node ? {
                ...normalizeArticlePrice(edge.node),
                ...(!!edge.accommodationBookingSuggestion) && { suggestion: edge.accommodationBookingSuggestion },
                withInnerContextNav: true
              } : undefined} />))
            }
            {data?.search.totalCount === 0 &&
              <InfoBox icon={<IconAlertSquareRoundedFilled />}
                       msg={intl.formatMessage(transDefs.empty)}>
                {intl.formatMessage(transDefs.emptyDetail)}
              </InfoBox>
            }
          </div>
          {!!data?.search &&
            <SearchedPagination data={data.search.paginationInfo}
                                onPaginate={onPaginate} />
          }
        </main>
        <aside ref={mapRef}
               className="search-map">
          <MapContainer className="map-wrapper"
                        center={[ 46.10859470576092, 7.085908637307684 ]}
                        zoom={11}
                        withBounds={{ padding: [ 50, 50 ] }}
                        clusterize={true}>
            {data?.search.accommodationMapMarkers.map((marker, i) => <MapAsyncPopupMarker key={i} marker={marker} />)}
          </MapContainer>
        </aside>
      </div>

    </Wrapper.Centered>);
};

export default SearchPage;
